// Analytics — Overview view (REC-88).
import { createFileRoute } from '@tanstack/react-router';
import { KpiCard } from '@/features/analytics/KpiCard';
import { useAnalyticsOverview } from '@/features/analytics/api';
import { buildDateRange, analyticsSearchSchema } from '@/features/analytics/dateRange';

export const Route = createFileRoute('/_authed/analytics/overview')({
  validateSearch: (raw: Record<string, unknown>) => {
    const parsed = analyticsSearchSchema.safeParse(raw);
    return parsed.success ? parsed.data : {};
  },
  component: OverviewPage,
});

function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function fmtCents(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function OverviewPage() {
  const rawSearch = Route.useSearch();
  const range = buildDateRange({
    preset: rawSearch.preset,
    from: rawSearch.from,
    to: rawSearch.to,
  });

  const { data, isLoading, isError } = useAnalyticsOverview(range);

  if (isLoading) {
    return (
      <div className="text-ink-soft text-sm py-8">Loading overview…</div>
    );
  }

  if (isError || !data) {
    return (
      <div
        className="rounded-xl p-4 text-sm"
        style={{
          background: 'var(--color-error-bg)',
          border: '1px solid var(--color-error-border)',
          color: 'var(--color-error-text)',
        }}
      >
        Failed to load analytics overview. The rollup data may not be available
        yet — try refreshing or triggering a manual rollup.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="analytics-overview-page">
      {/* Active users */}
      <section>
        <h2 className="font-serif text-lg mb-3">Active users</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
          <KpiCard label="DAU (anon)" value={fmt(data.dau_anon)} />
          <KpiCard label="DAU (signed-in)" value={fmt(data.dau_user)} />
          <KpiCard label="WAU (anon)" value={fmt(data.wau_anon)} />
          <KpiCard label="MAU (signed-in)" value={fmt(data.mau_user)} />
          <KpiCard label="Signups" value={fmt(data.signups)} testId="analytics-kpi-signups" />
        </div>
      </section>

      {/* Engagement */}
      <section>
        <h2 className="font-serif text-lg mb-3">Engagement</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-3 gap-3">
          <KpiCard label="Total events" value={fmt(data.total_events)} testId="analytics-kpi-total-events" />
          <KpiCard label="Recipes generated" value={fmt(data.recipes_generated)} testId="analytics-kpi-recipes-generated" />
          <KpiCard label="Cook mode completed" value={fmt(data.cooks_completed)} testId="analytics-kpi-cooks-completed" />
        </div>
      </section>

      {/* Business */}
      <section>
        <h2 className="font-serif text-lg mb-3">Business</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          <KpiCard label="MRR" value={fmtCents(data.mrr_usd_cents)} testId="analytics-kpi-mrr" />
        </div>
      </section>
    </div>
  );
}
